import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const ThemeContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_COLOR = '#0EA5E9'; // sky-500

// Convert hex (#RRGGBB) to {r,g,b}
const hexToRgb = (hex) => {
  const h = (hex || DEFAULT_COLOR).replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return { r, g, b };
};

// Mix color toward white (factor 0..1) or black
const mix = (rgb, target, factor) => ({
  r: Math.round(rgb.r * (1 - factor) + target.r * factor),
  g: Math.round(rgb.g * (1 - factor) + target.g * factor),
  b: Math.round(rgb.b * (1 - factor) + target.b * factor),
});

const rgbToHex = ({ r, g, b }) => {
  const c = (v) => v.toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
};

const applyThemeVars = (color) => {
  const root = document.documentElement;
  const base = hexToRgb(color);
  const light = mix(base, { r: 255, g: 255, b: 255 }, 0.15);
  const dark = mix(base, { r: 0, g: 0, b: 0 }, 0.18);
  const surface = mix(base, { r: 255, g: 255, b: 255 }, 0.92); // tres clair

  root.style.setProperty('--theme-primary', color || DEFAULT_COLOR);
  root.style.setProperty('--theme-primary-rgb', `${base.r}, ${base.g}, ${base.b}`);
  root.style.setProperty('--theme-primary-light', rgbToHex(light));
  root.style.setProperty('--theme-primary-dark', rgbToHex(dark));
  root.style.setProperty('--theme-primary-surface', rgbToHex(surface));
  root.style.setProperty('--theme-on-primary', '#ffffff');
};

export const ThemeProvider = ({ children }) => {
  const { user } = useAuth();
  const [themeColor, setThemeColorState] = useState(DEFAULT_COLOR);

  const refreshFromBackend = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/agence/settings`, { withCredentials: true });
      const c = (res.data?.theme_color || '').trim();
      if (c && /^#[0-9A-Fa-f]{6}$/.test(c)) {
        setThemeColorState(c);
        applyThemeVars(c);
        return c;
      }
    } catch (e) {
      // silent — public pages or unauth
    }
    applyThemeVars(DEFAULT_COLOR);
    return DEFAULT_COLOR;
  }, []);

  // Apply default early to avoid flash
  useEffect(() => {
    applyThemeVars(DEFAULT_COLOR);
  }, []);

  // Reload theme when user identity changes (login / logout / tenant switch)
  useEffect(() => {
    if (user && user !== false) {
      refreshFromBackend();
    } else {
      applyThemeVars(DEFAULT_COLOR);
    }
  }, [user, refreshFromBackend]);

  const setThemeColor = useCallback((c) => {
    if (c && /^#[0-9A-Fa-f]{6}$/.test(c)) {
      setThemeColorState(c);
      applyThemeVars(c);
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ themeColor, setThemeColor, refreshFromBackend }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // fallback safe default
    return { themeColor: DEFAULT_COLOR, setThemeColor: () => {}, refreshFromBackend: () => {} };
  }
  return ctx;
};
